import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Card, Descriptions, Tag, Space, Typography, Divider, Button, message, Skeleton, Table } from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  ClusterOutlined,
  SafetyCertificateOutlined,
  DesktopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getDeviceById } from '../../../../api/deviceService';

dayjs.extend(relativeTime);

const { Content } = Layout;
const { Title, Text } = Typography;

type License = {
  id: number;
  license_key: string;
  license_type: string;
  is_active: 'active' | 'expired' | 'suspended' | string;
  ip_address: string;
  issue_date: string;
  expires_at: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

type Group = {
  id: number;
  name: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

type User = {
  id: number;
  username: string;
  email: string;
  phone_number?: string;
  image?: string;
  bio?: string;
  deletedAt?: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

export type DeviceDetail = {
  id: number;
  device_type: 'Mobile' | 'STB' | string;
  device_serial: string;
  device_name: string;
  firmware_version: string;
  license_id: number;
  group_id: number;
  user_id: number;
  last_heartbeat: string;
  ip_address: string;
  mac_address: string;
  location: string;
  user: User;
  groups?: Group[];
  license: License;
  createdAt: string;
  updatedAt: string;
};

const heartbeatFreshMinutes = 5;

function statusColor(status?: string) {
  switch ((status || '').toLowerCase()) {
    case 'active':
      return 'green';
    case 'expired':
      return 'volcano';
    case 'suspended':
      return 'orange';
    default:
      return 'default';
  }
}

function deviceTypeIcon(type?: string) {
  if (!type) return <DesktopOutlined />;
  const t = type.toLowerCase();
  if (t.includes('stb')) return <ClusterOutlined />;
  if (t.includes('mobile') || t.includes('phone')) return <DesktopOutlined />;
  return <DesktopOutlined />;
}

const ViewDeviceDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState<boolean>(false);
  const [device, setDevice] = useState<DeviceDetail | undefined>(undefined);

  const numericId = useMemo(() => (id ? Number(id) : undefined), [id]);

  const online = useMemo(() => {
    if (!device?.last_heartbeat) return false;
    const diffMin = dayjs().diff(dayjs(device.last_heartbeat), 'minute');
    return diffMin <= heartbeatFreshMinutes;
  }, [device?.last_heartbeat]);

  const load = async () => {
    if (!numericId) return;
    setLoading(true);
    try {
      const detail = await getDeviceById(numericId);
      // If your API wraps data: setDevice(detail.data)
      setDevice(detail as unknown as DeviceDetail);
    } catch (err: any) {
      message.error(err?.message || 'Failed to fetch device');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!numericId) return;
    load(); // always fetch from API on mount and when :id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericId]);

  const handleBack = () => {
    const activeTab = (location.state as { activeTab?: string } | null)?.activeTab;
    if (activeTab) {
      navigate('/device-management', { state: { activeTab } });
      return;
    }

    navigate(-1);
  };

  const headerActions = (
    <Space wrap>
      <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
        Back
      </Button>
      <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
        Refresh
      </Button>
    </Space>
  );

  const fmt = (d?: string) => (d ? dayjs(d).format('YYYY-MM-DD HH:mm:ss') : '—');

  // Add table columns for device groups
  const groupColumns = [
    {
      title: 'Group Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <span className="font-medium">{name || '—'}</span>,
    },
    {
      title: 'Device Count',
      dataIndex: 'deviceCount',
      key: 'deviceCount',
      align: 'center' as const,
      render: (count: number) => <span>{count || '—'}</span>,
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy: string) => <span className="text-sm">{createdBy || '—'}</span>,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => <span className="text-sm">{fmt(date)}</span>,
    },
    {
      title: 'Updated By',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      render: (updatedBy: string) => <span className="text-sm">{updatedBy || '—'}</span>,
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string) => <span className="text-sm">{fmt(date)}</span>,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Content style={{ padding: 24 }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
            <Space align="center" size="large">
              {deviceTypeIcon(device?.device_type)}
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {device?.device_name || 'Device'}
                </Title>
                <Space size="small" wrap>
                  <Tag color={online ? 'green' : 'default'}>{online ? 'Online' : 'Offline'}</Tag>
                  <Tag icon={<SafetyCertificateOutlined />} color={statusColor(device?.license?.is_active ? 'active' : 'inactive')}>
                      {device?.license?.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </Tag>
                  <Tag>{device?.device_type}</Tag>
                </Space>
              </div>
            </Space>
            {headerActions}
          </Space>

          <Card>
            {loading && !device ? (
              <Skeleton active />
            ) : !device ? (
              <Text type="secondary">Device not found.</Text>
            ) : (
              <Space direction="vertical" size={24} style={{ width: '100%' }}>
                {/* Device Details */}
                <Divider>Device Details</Divider>
                <Descriptions column={3} size="middle" bordered>
                  <Descriptions.Item label="Device Type">{device.device_type}</Descriptions.Item>
                  <Descriptions.Item label="Device Name">{device.device_name || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Device Serial Number">{device.device_serial}</Descriptions.Item>
                  {device.device_type === 'STB' && (
                    <>
                      <Descriptions.Item label="IP Address">{device?.license?.ip_address || '—'}</Descriptions.Item>
                      {/* <Descriptions.Item label="MAC Address">{device.mac_address || '—'}</Descriptions.Item> */}
                      <Descriptions.Item label="Firmware Version">{device.firmware_version || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Location">{device.location || '—'}</Descriptions.Item>
                    </>
                  )}
                  <Descriptions.Item label="Last Heartbeat">
                    {device.last_heartbeat
                      ? `${dayjs(device.last_heartbeat).format('YYYY-MM-DD HH:mm:ss')} (${dayjs(
                          device.last_heartbeat
                        ).fromNow()})`
                      : '—'}
                  </Descriptions.Item>

                  <Descriptions.Item label="Created At">{fmt(device.createdAt)}</Descriptions.Item>
                  <Descriptions.Item label="Updated At">{fmt(device.updatedAt)}</Descriptions.Item>
                </Descriptions>

               {/* Device Group(s) */}
              <Divider>Device Groups</Divider>
              {Array.isArray(device.groups) && device.groups.length > 0 ? (
                <Table
                  columns={groupColumns}
                  dataSource={device.groups}
                  rowKey={(record) => record.id || Math.random()}
                  pagination={false}
                  size="small"
                  bordered
                  scroll={{ x: 800 }}
                />
              ) : (
                <Text type="secondary">This device is not in any group.</Text>
              )}


                {/* License Details */}
                <Divider>License Details</Divider>
                <Descriptions column={4} size="middle" bordered>
                  <Descriptions.Item label="License Key">{device.license?.license_key ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="License Type">{device.license?.license_type ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={statusColor(device.license?.is_active ? 'active' : 'inactive')}>
                      {device.license?.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </Tag>
                  </Descriptions.Item>
                </Descriptions>
                <Descriptions column={2} size="middle" bordered>
                  <Descriptions.Item label="Expire Date">
                    {device.license?.expires_at ? dayjs(device.license.expires_at).format('YYYY-MM-DD') : 'NEVER EXPIRES'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">{fmt(device.license?.createdAt)}</Descriptions.Item>
                  <Descriptions.Item label="Created By">{device.license?.createdBy ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Updated At">{fmt(device.license?.updatedAt)}</Descriptions.Item>
                  <Descriptions.Item label="Updated By">{device.license?.updatedBy ?? '—'}</Descriptions.Item>
                </Descriptions>

                {/* User Details */}
                {device.device_type !== 'STB' && (
                  <>
                    <Divider>User Details</Divider>
                    <Descriptions column={2} size="middle" bordered>
                      <Descriptions.Item label="Username">{device.user?.username ?? '—'}</Descriptions.Item>
                      <Descriptions.Item label="Email">
                        <span>{device.user?.email ?? '—'}</span>
                      </Descriptions.Item>
                      <Descriptions.Item label="Mobile">{device.user?.phone_number ?? '—'}</Descriptions.Item>
                      <Descriptions.Item label="Created At">{fmt(device.user?.createdAt)}</Descriptions.Item>
                      <Descriptions.Item label="Created By">{device.user?.createdBy ?? '—'}</Descriptions.Item>
                      <Descriptions.Item label="Updated At">{fmt(device.user?.updatedAt)}</Descriptions.Item>
                      <Descriptions.Item label="Updated By">{device.user?.updatedBy ?? '—'}</Descriptions.Item>
                    </Descriptions>
                  </>
                )}
              </Space>
            )}
          </Card>
        </Space>
      </Content>
    </Layout>
  );
};

export default ViewDeviceDetails;
